import React, { forwardRef, InputHTMLAttributes, ReactNode } from 'react'
import styled, { css } from 'styled-components'

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  $endAdornment?: ReactNode
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  ({ label, error, helperText, id, $endAdornment, ...rest }, ref) => {
    const inputId = id || rest.name
    return (
      <Field>
        {label && <Label htmlFor={inputId}>{label}</Label>}
        <InputWrap $hasAdornment={Boolean($endAdornment)}>
          <Input id={inputId} ref={ref} $hasError={Boolean(error)} {...rest} />
          {$endAdornment}
        </InputWrap>
        {(error || helperText) && (
          <Helper $isError={Boolean(error)}>{error || helperText}</Helper>
        )}
      </Field>
    )
  },
)
TextInput.displayName = 'TextInput'

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink2};
  white-space: nowrap;
`

const InputWrap = styled.div<{ $hasAdornment: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  ${({ $hasAdornment }) =>
    $hasAdornment &&
    css`
      input {
        padding-left: 2.625rem;
      }
    `}
`

const Input = styled.input<{ $hasError: boolean }>`
  width: 100%;
  min-height: 2.875rem;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid
    ${({ theme, $hasError }) => ($hasError ? theme.colors.danger : theme.colors.line)};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: 0.5rem 0.875rem;
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.ink};
  outline: none;
  direction: rtl;
  transition:
    border-color ${({ theme }) => theme.motion.fast},
    box-shadow ${({ theme }) => theme.motion.fast};

  &::placeholder {
    color: ${({ theme }) => theme.colors.ink4};
  }

  &:focus {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.danger : theme.colors.ink};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.line2};
    cursor: not-allowed;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 2.5rem;
  }
`

const Helper = styled.span<{ $isError: boolean }>`
  font-size: 0.75rem;
  color: ${({ theme, $isError }) => ($isError ? theme.colors.danger : theme.colors.ink3)};
`

export default TextInput
